import { useMutation, useQuery } from 'convex/react';
import { api } from '@/convex';
import type { Id } from '@/convex';
import type { FindingDoc } from './findings-types';

type Skip = 'skip';

export type FindingsPageQueryArgs =
  | {
      projectId: Id<'projects'>;
      pageIndex: number;
      pageSize: number;
      status?: string;
      source?: string;
      tokenFilter?: string;
      area?: string;
      code?: string;
      ruleId?: string;
      category?: string;
      severity?: string;
      searchQuery?: string;
      filePath?: string;
      actionability?: string;
      policyStatus?: string;
      decisionKind?: string;
      decisionKey?: string;
    }
  | Skip;

export type FindingsCountsQueryArgs =
  | {
      projectId: Id<'projects'>;
      source?: string;
    }
  | Skip;

export type FindingsGroupQueryArgs =
  | {
      projectId: Id<'projects'>;
      groupBy: 'file' | 'rule' | 'pr' | 'decision' | 'area';
      status?: string;
      source?: string;
      tokenFilter?: string;
      area?: string;
      code?: string;
      ruleId?: string;
      category?: string;
      severity?: string;
      searchQuery?: string;
      filePath?: string;
      actionability?: string;
      policyStatus?: string;
      decisionKind?: string;
      decisionKey?: string;
    }
  | Skip;

export function useFindingsData({
  pageArgs,
  countsArgs,
  groupArgs,
  projectLoading,
}: {
  pageArgs: FindingsPageQueryArgs;
  countsArgs: FindingsCountsQueryArgs;
  groupArgs: FindingsGroupQueryArgs;
  projectLoading: boolean;
}) {
  const findingsPage = useQuery(
    api.findingsPaginated.getByProjectPage,
    pageArgs,
  );
  const counts = useQuery(api.findings.getCountsByProject, countsArgs);
  const groupAggregate = useQuery(api.findingsGroups.getGroups, groupArgs);
  const repoScanHistory = useQuery(
    api.findingsHistory.getGovernanceTrend,
    pageArgs === 'skip'
      ? 'skip'
      : { projectId: pageArgs.projectId, source: 'repo-scan', limit: 1 },
  );

  const updateStatus = useMutation(api.findings.updateStatus);
  const bulkUpdateStatus = useMutation(api.findings.bulkUpdateStatus);
  const createWaiver = useMutation(api.findingsWaivers.createForFinding);
  const bulkLinkIssue = useMutation(api.findings.bulkLinkIssue);

  const isFirstPageLoading = pageArgs !== 'skip' && findingsPage === undefined;
  const repoScanHistoryLoading =
    pageArgs !== 'skip' && repoScanHistory === undefined;
  const isLoading =
    projectLoading ||
    (pageArgs !== 'skip' &&
      (isFirstPageLoading || counts === undefined || repoScanHistoryLoading));

  return {
    findingsPage,
    counts,
    groupAggregate,
    latestRepoScanRun: repoScanHistory?.items[0] ?? null,
    repoScanHistoryLoading,
    items: (findingsPage?.items ?? []) as unknown as FindingDoc[],
    isFirstPageLoading,
    isLoading,
    actions: {
      updateStatus,
      bulkUpdateStatus,
      createWaiver,
      bulkLinkIssue,
    },
  };
}
